/**
 * Reading a template-send refusal back on the screen.
 *
 * `whatsapp-send-template` stopped answering an opaque 500 on 2026-09-08: when it will not send,
 * it answers a non-2xx with `{ error, code, refused: true }`, and the code names one of eight
 * distinct things the operator can act on. The platform client hides that body behind a generic
 * `FunctionsHttpError` whose `.message` is "Edge Function returned a non-2xx status code", so a
 * screen that shows `error.message` shows the transport string and nothing else.
 *
 * This is the one place that unwraps it, so no screen has to know how.
 */

/** Keep in sync with `TemplateRefusalCode` in
 *  supabase/functions/whatsapp-send-template/resolve-send-target.ts. */
export const TEMPLATE_REFUSAL_CODES = [
  'conversation_required',
  'rehearsal_conversation',
  'seal_unreadable',
  'no_whatsapp_configuration',
  'channel_not_available',
  'ambiguous_account',
  'rehearsal_channel',
  'credentials_missing',
] as const;

export type TemplateRefusalCode = typeof TEMPLATE_REFUSAL_CODES[number];

export interface TemplateSendRefusal {
  code: TemplateRefusalCode;
  /** The English detail the function wrote, for logs and for the expandable detail line. */
  detail?: string;
  /** i18n key in the `conversations` namespace. */
  messageKey: string;
}

function isRefusalCode(v: unknown): v is TemplateRefusalCode {
  return typeof v === 'string' && (TEMPLATE_REFUSAL_CODES as readonly string[]).includes(v);
}

/**
 * Pull the refusal out of a `supabase.functions.invoke()` failure.
 *
 * Returns `undefined` when this was not a refusal — a Meta error, a network failure, an
 * unexpected 500 — so the caller keeps whatever it already did for those. Never throws.
 */
export async function readTemplateSendRefusal(err: unknown): Promise<TemplateSendRefusal | undefined> {
  if (!err || typeof err !== 'object') return undefined;
  const context = (err as { context?: unknown }).context;
  if (!context || typeof (context as Response).json !== 'function') return undefined;
  try {
    // .clone() so a body already read elsewhere does not throw here.
    const body = await (context as Response).clone().json();
    if (!body || typeof body !== 'object') return undefined;
    const code = (body as Record<string, unknown>).code;
    if (!isRefusalCode(code)) return undefined;
    const detail = (body as Record<string, unknown>).error;
    return {
      code,
      detail: typeof detail === 'string' ? detail : undefined,
      messageKey: `templateSend.refused.${code}`,
    };
  } catch {
    return undefined;
  }
}
